// components/ModalSeleccionarCliente.jsx

import React, { useState, useEffect } from 'react';
import api from '../services/api';
import '../styles/ModalSeleccionarCliente.css';
import { useFiscal } from '../hooks/useFiscal';

const ModalSeleccionarCliente = ({ isOpen, onClose, onSelect, clientes }) => {
  const fiscal = useFiscal();
  const [busqueda, setBusqueda] = useState('');
  const [clienteSeleccionado, setClienteSeleccionado] = useState(null);
  const [asignarNcf, setAsignarNcf] = useState(false);
  const [tiposNcf, setTiposNcf] = useState([]);
  const [tipoNcfSeleccionado, setTipoNcfSeleccionado] = useState('');
  const [ncfDisponible, setNcfDisponible] = useState(null);
  const [cargandoNcf, setCargandoNcf] = useState(false);

  useEffect(() => {
    if (!isOpen) {
      setBusqueda('');
      setClienteSeleccionado(null);
      setAsignarNcf(false);
      setTipoNcfSeleccionado('');
      setNcfDisponible(null);
    } else {
      cargarTiposNcf();
    }
  }, [isOpen]);

  const cargarTiposNcf = async () => {
    try {
      const response = await api.get('/ncf/tipos');
      setTiposNcf(response.data);
    } catch (error) {
      console.error('Error cargando tipos NCF:', error);
    }
  };

  const cargarNcfDisponible = async (tipo) => {
    try {
      setCargandoNcf(true);
      const response = await api.get('/ncf/disponibilidad');
      const disponibilidad = response.data.find(d => d.tipo_ncf === tipo);
      
      if (disponibilidad) {
        setNcfDisponible(disponibilidad);
      } else {
        setNcfDisponible(null);
      }
    } catch (error) {
      console.error('Error cargando NCF disponible:', error);
      setNcfDisponible(null);
    } finally {
      setCargandoNcf(false);
    }
  };

  useEffect(() => {
    if (tipoNcfSeleccionado) {
      cargarNcfDisponible(tipoNcfSeleccionado);
    } else {
      setNcfDisponible(null);
    }
  }, [tipoNcfSeleccionado]);

  if (!isOpen) return null;

  const clientesFiltrados = clientes.filter(cliente =>
    cliente.nombre.toLowerCase().includes(busqueda.toLowerCase()) ||
    (cliente.rnc_cedula && cliente.rnc_cedula.includes(busqueda)) ||
    (cliente.telefono && cliente.telefono.includes(busqueda))
  );

  const handleSeleccionar = () => {
    if (clienteSeleccionado) {
      const datosFactura = {
        cliente: clienteSeleccionado,
        ncf: asignarNcf ? {
          tipo: tipoNcfSeleccionado,
          generar: true
        } : null
      };
      onSelect(datosFactura);
      onClose();
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content-cliente" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="modal-header-cliente">
          <h2>💰 Seleccionar Cliente para Factura</h2>
          <button className="modal-close-btn" onClick={onClose}>✕</button>
        </div>

        {/* Buscador */}
        <div className="modal-search-cliente">
          <input
            type="text"
            placeholder="🔍 Buscar cliente por nombre, RNC o teléfono..."
            value={busqueda}
            onChange={(e) => setBusqueda(e.target.value)}
            autoFocus
          />
        </div>

        {/* Sección NCF (Opcional) */}
        <div className="modal-ncf-section">
          <div className="ncf-checkbox">
            <input
              type="checkbox"
              id="asignar-ncf"
              checked={asignarNcf}
              onChange={(e) => setAsignarNcf(e.target.checked)}
            />
            <label htmlFor="asignar-ncf">
              📄 Asignar NCF (Comprobante Fiscal) ahora
            </label>
          </div>

          {asignarNcf && (
            <div className="ncf-selector">
              <div className="ncf-tipo-selector">
                <label>Tipo de NCF:</label>
                <select
                  value={tipoNcfSeleccionado}
                  onChange={(e) => setTipoNcfSeleccionado(e.target.value)}
                >
                  <option value="">-- Seleccionar Tipo --</option>
                  {tiposNcf.map(tipo => (
                    <option key={tipo.codigo} value={tipo.codigo}>
                      B{tipo.codigo} - {tipo.nombre}
                    </option>
                  ))}
                </select>
              </div>

              {tipoNcfSeleccionado && (
                <div className="ncf-disponibilidad">
                  {cargandoNcf ? (
                    <p className="ncf-loading">⏳ Cargando disponibilidad...</p>
                  ) : ncfDisponible ? (
                    <>
                      {ncfDisponible.vencido ? (
                        <div className="ncf-alerta error">
                          ⚠️ Secuencia vencida ({ncfDisponible.fecha_vencimiento})
                        </div>
                      ) : ncfDisponible.disponibles < 50 ? (
                        <div className="ncf-alerta warning">
                          ⚠️ Quedan solo {ncfDisponible.disponibles} NCF disponibles
                        </div>
                      ) : (
                        <div className="ncf-info success">
                          ✅ Disponibles: {ncfDisponible.disponibles} NCF
                          <br />
                          📅 Vencimiento: {new Date(ncfDisponible.fecha_vencimiento).toLocaleDateString('es-DO')}
                        </div>
                      )}
                    </>
                  ) : (
                    <div className="ncf-alerta error">
                      ❌ No hay secuencia NCF disponible para este tipo
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Lista de clientes */}
        <div className="modal-body-cliente">
          {clientesFiltrados.length === 0 ? (
            <div className="empty-state">
              <p>No se encontraron clientes</p>
            </div>
          ) : (
            <div className="clientes-grid">
              {clientesFiltrados.map((cliente) => (
                <div
                  key={cliente.id}
                  className={`cliente-card ${clienteSeleccionado?.id === cliente.id ? 'selected' : ''}`}
                  onClick={() => setClienteSeleccionado(cliente)}
                >
                  <div className="cliente-card-header">
                    <div className="cliente-icon">👤</div>
                    <div className="cliente-check">
                      {clienteSeleccionado?.id === cliente.id && '✓'}
                    </div>
                  </div>
                  <div className="cliente-card-body">
                    <h3 className="cliente-nombre">{cliente.nombre}</h3>
                    {cliente.rnc_cedula && (
                      <p className="cliente-info">
                        <span className="info-label">{fiscal.id_fiscal_empresa_label || 'RNC'}/{fiscal.id_fiscal_persona_label || 'Cédula'}:</span>
                        <span className="info-value">{cliente.rnc_cedula}</span>
                      </p>
                    )}
                    {cliente.telefono && (
                      <p className="cliente-info">
                        <span className="info-label">📞</span>
                        <span className="info-value">{cliente.telefono}</span>
                      </p>
                    )}
                    {cliente.email && (
                      <p className="cliente-info">
                        <span className="info-label">✉️</span>
                        <span className="info-value">{cliente.email}</span>
                      </p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="modal-footer-cliente">
          <button className="btn-cancelar" onClick={onClose}>
            Cancelar
          </button>
          <button
            className="btn-confirmar"
            onClick={handleSeleccionar}
            disabled={
              !clienteSeleccionado || 
              (asignarNcf && (!tipoNcfSeleccionado || !ncfDisponible || ncfDisponible.vencido))
            }
          >
            Generar Factura
          </button>
        </div>
      </div>
    </div>
  );
};

export default ModalSeleccionarCliente;